<template>
  <div>
    <Line :data="timeSeriesData" :options="lineOptions" />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { Line } from 'vue-chartjs'
import { eachDayOfInterval, format, max, min } from 'date-fns'

const props = defineProps({
  datasets: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Object,
    default: () => ({}),
  },
})

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
)

const timeSeriesData = ref({
  labels: [],
  datasets: [
    {
      // label: props.label,
      data: [],
    },
  ],
})

const lineOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  ...props.options,
  interaction: {
    mode: 'index',
    intersect: false,
    ...props.options.interaction,
  },
  datasets: {
    line: {
      backgroundColor: 'oklch(51.1% 0.262 276.966)', // indigo-600
      borderColor: 'oklch(78.5% 0.115 274.713)', // indigo-300
      tension: 0.15,
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: 'day',
        tooltipFormat: 'EEEE, dd/MM/yyyy',
        displayFormats: {
          day: 'EEE, LLL d',
        },
      },
      ...props.options.scales?.x,
    },
    y: {
      min: 0,
      grace: 10,
      ticks: {
        stepSize: 10,
      },
      ...props.options.scales?.y,
    },
  },
  plugins: {
    datalabels: false,
    ...props.options.plugins,
    legend: {
      display: false,
      ...props.options.plugins?.legend,
    },
  },
}))

watch(
  () => props.datasets,
  (datasets) => {
    const allDates = []
    datasets?.forEach(({ data }) => {
      if (data) {
        allDates.push(...Object.keys(data))
      }
    })

    if (allDates.length) {
      const startDate = min(allDates)
      const endDate = max(allDates)

      const dateKeys = eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => format(date, 'yyyy-MM-dd'))

      timeSeriesData.value = {
        labels: dateKeys.map((date) => new Date(date)),
        datasets: datasets.map(({ data, ...set }) => ({
          ...set,
          data: dateKeys.map((date) => data[date] ?? 0),
        })),
      }
    }
  },
  { immediate: true },
)
</script>
